import * as React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";

import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/core/shared/api";
import { Button } from "src/library/components/Button";
import {
  IPaymentCard,
  usePaymentApi,
  IPaymentCardBranch
} from "src/modules/payment/api/payment.api";
import { IDrop } from "src/core/drops/api/drops.api";

interface RemoveBranchModalProps extends Omit<BasicModalProps, "title"> {
  terminology: ITerminology;
  paymentMethod: IPaymentCard;
  drop: IDrop | null;
  onRemoveSuccess?: (paymentCardBranch: IPaymentCardBranch) => void;
}

/**
 * Modal hanlding removing a branch association from a card
 */
const RemoveBranchModal: React.ReactFCWithChildren<RemoveBranchModalProps> = ({
  open,
  onDismiss,
  terminology,
  paymentMethod,
  drop,
  onRemoveSuccess
}) => {
  const [ deletePaymentCardBranchRes, deletePaymentCardBranchReq ] = usePaymentApi("DELETE_PAYMENT_CARD_BRANCH");

  React.useEffect(() => {
    if (deletePaymentCardBranchRes.error) {
      toast.error(deletePaymentCardBranchRes.error.message);
    }

    if (deletePaymentCardBranchRes.data) {
      if (onRemoveSuccess) {
        onRemoveSuccess(deletePaymentCardBranchRes.data);
      }

      toast.success("Successfully set card as default payment card.");
    }
  }, [ deletePaymentCardBranchRes, onRemoveSuccess ]);

  // handle when user confirms request to remove associated branch
  const handleRemoveConfirmClick = React.useCallback(() => {
    if (!drop) {
      toast.error("Drop not set. Drop must be known in order to remove from association from payment method.");
    }

    if (drop) {
      deletePaymentCardBranchReq({
        paymentMethodId: paymentMethod.id,
        branchId: drop.id
      });
    }
  }, [
    deletePaymentCardBranchReq,
    drop,
    paymentMethod.id
  ]);

  return (
    <StyledRemoveBranchModal
      open={open}
      onDismiss={onDismiss}
      title={`Remove ${_.startCase(terminology.drop.singular.text)} Association`}
    >
      <>
        <div className="message">
          <Text>
            {`Are you sure that you want to remove the (${drop?.branchNumber}) ${drop?.name} ${terminology.drop.singular.text} 
            from having access to card ending ${paymentMethod.last4}?`}
          </Text>
        </div>

        <div className="actions">
          <Button
            disabled={deletePaymentCardBranchRes.pending}
            onClick={() => onDismiss()}
            $buttonType="secondary"
          >
            No, cancel
          </Button>

          <Button
            loading={deletePaymentCardBranchRes.pending}
            $buttonType="primary"
            onClick={handleRemoveConfirmClick}
          >
            Yes, remove
          </Button>
        </div>
      </>
    </StyledRemoveBranchModal>
  );
};

export default RemoveBranchModal;

const StyledRemoveBranchModal = styled(BasicModal)`
  ${({ theme }) => css`
    max-width: 440px;

    .message {
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .actions {
      display: flex;
      justify-content: space-between;
    }
  `}
`;
